import { useEffect, useMemo, useState } from "react";
import {
  Avatar,
  Checkbox,
  Container,
  FormControlLabel,
  FormGroup,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { useTranslation } from "react-i18next";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import { Link } from "react-router-dom";
import CallMadeOutlinedIcon from "@mui/icons-material/CallMadeOutlined";
import { useGetJobAdsQuery } from "../../../rq/hooks/useJob.mutation";
import { BusinessCenterOutlined } from "@mui/icons-material";
import dayjs from "dayjs";
import CircularIndeterminate from "../../../components/CircularIndeterminate/CircularIndeterminate";
import { StateType } from "../../../interfaces/JobOffers.model";
import { LogoAsset } from "../Home/types";

type JobAdsBoardProps = {
  onVisibleLogosChange?: (logos: LogoAsset[]) => void;
};

const JobAdsBoard = ({ onVisibleLogosChange }: JobAdsBoardProps) => {
  const { t, i18n } = useTranslation();

  const [searchWord, setSearchWord] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [checkboxValue, setCheckboxValue] = useState({
    cdd: false,
    internship: false,
    jobStudent: false,
  });
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [employmentSwitcher, setEmploymentSwitcher] = useState<{
    [key: string]: string;
  }>(() => ({
    "Contrat à Durée Déterminée": t("cdd"),
    Stage: t("internship"),
    "Job étudiant": t("job_student"),
  }));

  const typeOfEmployments = useMemo(
    () => [
      {
        id: 1,
        original: "Contrat à Durée Déterminée",
        name: "cdd",
        label: t("cdd"),
      },
      { id: 2, original: "Stage", name: "internship", label: t("internship") },
      {
        id: 3,
        original: "Job étudiant",
        name: "jobStudent",
        label: t("job_student"),
      },
    ],
    [t],
  );

  const { data: jobs = [], isLoading: jobsLoading } = useGetJobAdsQuery({
    employmentTypes: selectedIds,
    searchWord: debouncedSearch || undefined,
  });

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchWord);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchWord]);

  useEffect(() => {
    if (!onVisibleLogosChange) {
      return;
    }

    if (!jobs || jobs.length === 0) {
      onVisibleLogosChange([]);
      return;
    }

    const uniqueLogos = new Map<string, string>();

    jobs.forEach((job: any) => {
      if (job.businessLogoPath) {
        const label =
          job.businessName || job.title || t("home.companies_cards.logo_alt");

        if (!uniqueLogos.has(job.businessLogoPath)) {
          uniqueLogos.set(job.businessLogoPath, label);
        }
      }
    });

    onVisibleLogosChange(
      Array.from(uniqueLogos.entries()).map(([src, label]) => ({
        src,
        label,
      })),
    );
  }, [jobs, onVisibleLogosChange, t]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = event.target;

    setCheckboxValue((prevState) => {
      const newState = {
        ...prevState,
        [name as keyof typeof prevState]: checked,
      };

      const nextSelectedIds = Object.keys(newState)
        .filter((key) => newState[key as keyof typeof newState])
        .map((key) => typeOfEmployments.find((item) => item.name === key)?.id)
        .filter((id): id is number => id !== undefined);

      setSelectedIds(nextSelectedIds);

      return newState;
    });
  };

  useEffect(() => {
    setEmploymentSwitcher({
      "Contrat à Durée Déterminée": t("cdd"),
      Stage: t("internship"),
      "Job étudiant": t("job_student"),
      "Fixed-term contract": t("cdd"),
      Internship: t("internship"),
      "Student job": t("job_student"),
    });
  }, [i18n.language, t]);

  return (
    <Container className="px-4 py-6 flex flex-col gap-6">
      <div className="flex justify-between items-center">
        <Typography className="text-[#72C2CC]  text-sm lg:text-xl">
          {t("current_jobs")}
        </Typography>
        <Typography className="text-[#A7A7A7] text-base">
          {jobs.length} {t("results")}
        </Typography>
      </div>
      <TextField
        size="small"
        placeholder={t("search_jobs")}
        value={searchWord}
        onChange={(e) => setSearchWord(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon className="text-[#A7A7A7]" />
            </InputAdornment>
          ),
        }}
        className="bg-white rounded-xl"
        sx={{
          "& .MuiOutlinedInput-root": {
            borderRadius: "0.75rem",
          },
        }}
      />
      <div className="grid lg:grid-cols-4 gap-12 h-fit">
        <div className="p-4 flex flex-col gap-2 h-fit bg-white rounded-xl">
          <Typography className="font-bold">
            {t("type_of_employment")}
          </Typography>
          <FormGroup className="ml-3">
            {typeOfEmployments.map(({ id, label, name }) => (
              <FormControlLabel
                key={id}
                control={
                  <Checkbox
                    checked={checkboxValue[name as keyof StateType]}
                    onChange={handleChange}
                    name={name}
                    value={id}
                  />
                }
                label={label}
              />
            ))}
          </FormGroup>
        </div>

        <div className="lg:col-span-3 w-full">
          {jobsLoading ? (
            <CircularIndeterminate />
          ) : (
            <div className="w-full grid lg:grid-cols-2 gap-4">
              {jobs.map(
                ({
                  businessLogoPath,
                  businessName,
                  title,
                  publishDate,
                  employmentTypes,
                  id,
                }) => (
                  <Link
                    to={`/job-offer/${id}`}
                    className="rounded-xl relative w-full p-2 border-2 border-solid bg-white  border-[#ECECEC]"
                    key={id}
                  >
                    <div className="flex items-center border-b-2 border-b-[#EFEBFF] border-solid my-1 py-1">
                      <div className="flex gap-1">
                        {businessLogoPath ? (
                          <Avatar src={businessLogoPath} variant="rounded" />
                        ) : (
                          <Avatar src="" variant="rounded">
                            <BusinessCenterOutlined />
                          </Avatar>
                        )}
                        <div className="flex flex-col">
                          <Typography className="text-[#9D9D9D] text-sm">
                            {businessName}
                          </Typography>
                          <Typography className="font-bold text-sm">
                            {title}
                          </Typography>
                          <div className="flex items-center gap-1">
                            <CalendarTodayOutlinedIcon className="fill-[#C6C6C6] text-sm" />
                            <Typography className="text-[#4A4D4B] text-sm">
                              {t("published_on")}{" "}
                              {dayjs(publishDate).format("DD/MM/YYYY")}
                            </Typography>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-1 justify-between items-center">
                      <Typography className="text-[#4A4D4B] flex-wrap text-sm flex gap-1 flex-1">
                        {employmentTypes.map((type) => (
                          <span
                            key={type}
                            className="bg-[#F0F0F0] px-2 py-1 h-fit rounded-lg text-xs mr-1"
                          >
                            {employmentSwitcher[type] || type}
                          </span>
                        ))}
                      </Typography>
                      <div className="flex items-center gap-2">
                        <Link
                          to={`/job-offer/${id}`}
                          className="flex items-center gap-1"
                        >
                          <Typography className="text-sm truncate text-[#3F6FE8] underline">
                            {t("more_details")}
                          </Typography>
                          <CallMadeOutlinedIcon className="text-sm fill-[#3F6FE8]" />
                        </Link>
                      </div>
                    </div>
                  </Link>
                ),
              )}
            </div>
          )}
        </div>
      </div>
    </Container>
  );
};

export default JobAdsBoard;
